import React, { useMemo } from 'react';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import { usePublish } from '@atlaskit/rovo-triggers';
import { COMMAND_PALETTE_SECTIONS } from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { COMMAND_PALETTE_QUICK_ACTIONS_PRIORITY } from '@atlassian/jira-command-palette-common/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	type Command,
	CommandActionType,
	type CommandActionPerformContext,
} from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useRovoEntitlementResource } from '@atlassian/jira-router-resources-rovo-entitlement/src/services/index.tsx';

import { messages } from './messages.tsx';

export const useAskRovoCommand = (): Command | null => {
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const { data: rovoEntitlementData } = useRovoEntitlementResource();

	const askRovoCommand = useMemo((): Command | null => {
		// AI entitlement check this will prevent the command from appearing if tenant doesn't have ai enabled
		if (!rovoEntitlementData?.isRovoEnabled) {
			return null;
		}

		return {
			id: 'ask-rovo-command',
			name: formatMessage(messages.askRovoCommandName),
			keywords: formatMessage(messages.askRovoCommandDescription),
			section: COMMAND_PALETTE_SECTIONS.quickActions,
			priority: COMMAND_PALETTE_QUICK_ACTIONS_PRIORITY.askRovo,
			alwaysShowWithSearchResults: true,
			components: {
				LeftIcon: () => <RovoIcon label="" />,
			},
			analytics: {
				action: 'askRovo',
			},
			primaryAction: {
				type: CommandActionType.PERFORM,
				perform: (context: CommandActionPerformContext) => {
					// Open Rovo chat with the user's search query
					// if you don't want auto-submit use insert-prompt in type and placeholderType: 'generic' in data:{}
					publish({
						type: 'chat-new',
						source: 'command-palette',
						data: {
							name: 'Command palette chat',
							dialogues: [],
							prompt: context?.search?.trim(),
						},
					});
				},
			},
		};
	}, [formatMessage, publish, rovoEntitlementData?.isRovoEnabled]);

	return askRovoCommand;
};
